import { getActiveProfile } from "./auth-store";

export type ApiErrorBody = {
  error?: string;
  message?: string;
  details?: Record<string, unknown> | null;
};

export class ApiError extends Error {
  public status: number;
  public code: string;
  public details: Record<string, unknown> | null;

  constructor(status: number, body: ApiErrorBody | null, fallback: string) {
    super(body?.message ?? fallback);
    this.name = "ApiError";
    this.status = status;
    this.code = body?.error ?? "UNKNOWN";
    this.details = body?.details ?? null;
  }
}

// -----------------------------------------------------------------------------
// Global 401-Handler
// -----------------------------------------------------------------------------
// React Query's default error boundary just propagates the rejection — useful
// for ErrorBanner, but not enough: a 401 means the persisted token is dead and
// the UI is showing pages the user can't actually use. We register one
// callback here (wired from main.tsx after the router is built) and fire it
// from the API client whenever a request comes back 401.
//
// Single-flight guard: a 401-cascade during dashboard refresh shouldn't
// re-trigger the logout 5 times in 50 ms. The flag self-resets via setTimeout
// so re-login + immediate-failure stays detectable.

let onUnauthorized: (() => void) | null = null;
let unauthorizedFiring = false;

/**
 * Register a callback fired on every 401 response. Idempotent — call once at
 * app bootstrap from main.tsx with `() => { authStore.logoutCurrent(); router.navigate("/login"); }`.
 */
export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

function fireUnauthorized(): void {
  if (unauthorizedFiring || !onUnauthorized) return;
  unauthorizedFiring = true;
  try {
    onUnauthorized();
  } finally {
    // Reset shortly after — long enough that a 401-stampede from parallel
    // queries dedupes, short enough that the next legitimate 401 also fires.
    setTimeout(() => {
      unauthorizedFiring = false;
    }, 500);
  }
}

type RequestOptions = {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  headers?: Record<string, string>;
  idempotencyKey?: string;
  /** Override the API base URL (mainly for the login probe). */
  apiUrlOverride?: string;
  /** Override the bearer token (mainly for the login probe). */
  tokenOverride?: string;
  /**
   * Force cookie-credentials mode for this request (SSO). When unset it is
   * derived from the active profile's mode ('sso' -> include). Used by the
   * /auth/me + /auth/logout calls during the SSO callback, before a profile
   * exists in the store.
   */
  withCredentials?: boolean;
  /**
   * Skip the global 401-handler for this request. Used by the SSO callback's
   * /me probe: a 401 there means "not a member", which the callback surfaces as
   * its own error UI instead of a "session expired" toast + logout.
   */
  suppressUnauthorized?: boolean;
  /** Set to true to receive the raw Response (for streams / file downloads). */
  raw?: boolean;
  signal?: AbortSignal;
};

function buildUrl(base: string, path: string, query?: RequestOptions["query"]): string {
  const url = new URL(path.replace(/^\/+/, "/"), base.endsWith("/") ? base : `${base}/`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === "") continue;
      url.searchParams.append(k, String(v));
    }
  }
  return url.toString();
}

export async function apiRequest<T = unknown>(
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const profile = getActiveProfile();
  const apiUrl = opts.apiUrlOverride ?? profile?.apiUrl;
  const token = opts.tokenOverride ?? profile?.token;
  // SSO/Cookie-Modus: den hwidalloc.session-Cookie mitsenden, KEINEN
  // Authorization-Header. Explizit via withCredentials (Callback-Probe) oder
  // abgeleitet aus dem aktiven Profil-Modus.
  const useCredentials = opts.withCredentials ?? (profile?.mode === "sso");
  if (!apiUrl) {
    throw new ApiError(0, null, "No API URL configured. Please log in.");
  }

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(opts.headers ?? {}),
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  // CSRF: cookie-authentifizierte (SSO) Requests tragen einen nicht-safelisted
  // Header, damit sie preflight-pflichtig werden und die CORS-Origin-Whitelist
  // greift (der Server erzwingt das fuer Cookie+unsafe). Im Token-Modus harmlos.
  if (useCredentials) headers["X-Requested-With"] = "XMLHttpRequest";
  if (opts.idempotencyKey) headers["Idempotency-Key"] = opts.idempotencyKey;

  let body: BodyInit | undefined;
  if (opts.body !== undefined && opts.body !== null) {
    if (opts.body instanceof FormData || opts.body instanceof Blob) {
      body = opts.body;
    } else {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify(opts.body);
    }
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(apiUrl, path, opts.query), {
      method: opts.method ?? "GET",
      headers,
      body,
      credentials: useCredentials ? "include" : "same-origin",
      signal: opts.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ApiError(
      0,
      null,
      err instanceof Error ? `Network error: ${err.message}` : "Network error",
    );
  }

  if (opts.raw) {
    if (!response.ok) {
      const bodyText = await response.text().catch(() => "");
      let parsed: ApiErrorBody | null = null;
      try {
        parsed = bodyText ? (JSON.parse(bodyText) as ApiErrorBody) : null;
      } catch {
        // ignore
      }
      // 401 from a real API call → persisted token is dead. The login-probe
      // sets tokenOverride, so we skip the global handler there (the login
      // form surfaces its own error).
      if (response.status === 401 && !opts.tokenOverride && !opts.suppressUnauthorized) {
        fireUnauthorized();
      }
      throw new ApiError(response.status, parsed, `HTTP ${response.status}`);
    }
    return response as unknown as T;
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!response.ok) {
    const bodyObj =
      parsed && typeof parsed === "object" ? (parsed as ApiErrorBody) : null;
    if (response.status === 401 && !opts.tokenOverride && !opts.suppressUnauthorized) {
      fireUnauthorized();
    }
    throw new ApiError(response.status, bodyObj, `HTTP ${response.status}`);
  }

  return parsed as T;
}

export function newIdempotencyKey(): string {
  return `ui-${crypto.randomUUID()}`;
}
